import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Avatar,
	Box,
	Button,
	Flex,
	HStack,
	MaximizeOutline,
	MenuAlt2Outline,
	MinimizeOutline,
	SkeletonText,
	Spacer,
	Text,
	XOutline,
} from '@bonusly/design-system';

import type { BizyPeopleBrowserEntry, BizyPersonMoment } from '../../../lib/bizy/types';
import { useBizyMarkMomentSeenMutation, useBizyMomentFeedbackMutation, usePersonMoments } from '../../../queries/bizy-people';
import { Bizy } from '../../bizy';
import { RelativeTime } from '../../datetime';
import { BizyHeaderIconButton, BizySidePanelHeader } from '../bizy-side-panel-header';
import { useBizyPeopleEntries } from '../hooks/use-bizy-people-entries';
import { buildRecognizeMomentChatPayload } from '../lib/build-recognize-moment-chat-payload';
import { useBizyPendingChatMessageStore } from '../store/use-bizy-pending-chat-message-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { BizyFeedbackModal, type BizyFeedbackCategory } from './bizy-feedback-modal';
import { BizyMessageFeedback } from './bizy-message-feedback';
import { BizySourceIcon } from './bizy-source-icon';

// Person detail view (BIZY-637). Shown in the main column in place of the chat
// when a People Browser entry is opened. Shows the active moment as a card
// (avatar/name/time, title + description, source, x-of-y, Skip / Let's dive in)
// and the person's previous moments in an accordion below. Skip advances through
// the sidebar's up-to-5 entries (snapshotted on open so the count is stable);
// the last Skip returns to the chat.

function toDate(raw: string | null | undefined): Date | undefined {
	if (!raw) return undefined;
	const d = new Date(raw);
	return Number.isNaN(d.getTime()) ? undefined : d;
}

// Previous-memories rows show this many entries until "All memories…" expands
// the list (BIZY-671).
const PREVIOUS_MEMORIES_PREVIEW_COUNT = 5;

// Negative-feedback categories tailored to an observed moment (BIZY-734).
// Unlike the chat-response defaults, these describe what's wrong with an
// observation (not a reply), so "Not what I asked for" / "Slow or buggy" are
// dropped and "Not relevant or noteworthy" is added. The `id`s must match
// `ObservedMoment::FEEDBACK_CATEGORIES` on the backend.
const MOMENT_FEEDBACK_CATEGORIES: readonly BizyFeedbackCategory[] = [
	{ id: 'inaccurate', labelKey: 'feedback.momentCategories.inaccurate' },
	{ id: 'not_relevant', labelKey: 'feedback.momentCategories.notRelevant' },
	{ id: 'tone', labelKey: 'feedback.momentCategories.tone' },
	{ id: 'safety', labelKey: 'feedback.momentCategories.safety' },
	{ id: 'other', labelKey: 'feedback.momentCategories.other' },
];

// Strip the person's leading first name from a memory headline and capitalize
// the verb that follows, e.g. "Kevin pushed your thinking…" → "Pushed your
// thinking…" (BIZY-671). Headlines that don't start with the first name (or
// where nothing follows it) pass through unchanged.
function stripLeadingFirstName(headline: string, personName: string): string {
	const firstName = personName.trim().split(/\s+/)[0];
	if (!firstName || !headline.startsWith(`${firstName} `)) return headline;
	const rest = headline.slice(firstName.length + 1).trimStart();
	return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : headline;
}

// Deterministic per-moment pseudo-random in [0, 1). Keeps a ghost card's tilt
// stable across re-renders while each newly revealed card gets its own
// rotation/offset as you page through the deck.
function momentJitter(seed: string, salt: number): number {
	let hash = salt;
	for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
	return (hash >>> 0) / 4294967296;
}

// Soft ambient glow behind the card, from the BIZY-665 design (teal/50,
// blue/50, purple/100 ellipses blurred 100px over the surface).
const GLOW_BLOBS = [
	{ bg: 'teal.50', top: '8%', left: '2%', w: '55%', h: '45%' },
	{ bg: 'blue.50', top: '0%', left: '22%', w: '55%', h: '45%' },
	{ bg: 'purple.100', top: '12%', left: '48%', w: '50%', h: '40%' },
];

interface BizyPersonViewProps {
	showFullscreenToggle?: boolean;
	// Desktop MFE (Electron): render as a full-surface view — the header doubles
	// as the window drag region and the top padding is trimmed so content sits at
	// the very top. Off by default so the web side panel is unchanged.
	fullSurface?: boolean;
	// Override for the close (X) control. The web panel closes the whole panel;
	// the desktop MFE (always full-window) just clears the viewed person instead.
	onClose?: () => void;
}

export function BizyPersonView({ showFullscreenToggle = true, fullSurface = false, onClose }: BizyPersonViewProps) {
	const { t } = useTranslation('admin_bizy');
	const viewedPerson = useBizySidePanelStore((state) => state.viewedPerson);
	const clearViewedPerson = useBizySidePanelStore((state) => state.clearViewedPerson);
	const viewPerson = useBizySidePanelStore((state) => state.viewPerson);
	const showHistory = useBizySidePanelStore((state) => state.showHistory);
	const isFullscreen = useBizySidePanelStore((state) => state.isFullscreen);
	const toggleFullscreen = useBizySidePanelStore((state) => state.toggleFullscreen);
	const closePanel = useBizySidePanelStore((state) => state.close);
	const isPeopleBrowserCollapsed = useBizySidePanelStore((state) => state.isPeopleBrowserCollapsed);
	const togglePeopleBrowserCollapsed = useBizySidePanelStore((state) => state.togglePeopleBrowserCollapsed);
	const sendChatMessage = useBizyPendingChatMessageStore((state) => state.sendChatMessage);

	const { track } = useAnalytics();
	const { entries } = useBizyPeopleEntries(true);
	const { mutate: markSeen } = useBizyMarkMomentSeenMutation();
	const { mutate: submitMomentFeedback } = useBizyMomentFeedbackMutation();
	const { data: momentsData, isLoading: isLoadingMoments } = usePersonMoments(viewedPerson?.id);

	// Negative feedback opens the shared category/details modal before it
	// submits (full parity with message feedback); positive/clear submit
	// immediately. Keyed by observed moment id.
	const [feedbackMomentId, setFeedbackMomentId] = useState<string | null>(null);
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

	// A `?bizy=noticed.<slug>` deep link (BIZY-680) opens the person with only an
	// id (from history state) — no moment selected. Once the moments load, default
	// to the newest so the card and paging have a concrete anchor, filling in the
	// display name from the same response. The URL keeps its cosmetic slug (the
	// moment is not encoded), and re-defaulting the same person is a no-op.
	useEffect(() => {
		if (!viewedPerson || viewedPerson.observedMomentId) return;
		const newestMoment = momentsData?.moments[0];
		if (!newestMoment) return;
		viewPerson({
			id: viewedPerson.id,
			name: viewedPerson.name || momentsData?.person?.name || '',
			observedMomentId: newestMoment.id,
		});
	}, [viewedPerson, momentsData, viewPerson]);

	// Snapshot the ordered sidebar entries on open so "x of y" and Skip stay
	// stable while marking-seen refetches the live list.
	const snapshotRef = useRef<BizyPeopleBrowserEntry[] | null>(null);
	if (snapshotRef.current === null && entries.length > 0) snapshotRef.current = entries;
	const snapshot = snapshotRef.current ?? entries;

	const activeObservedMomentId = viewedPerson?.observedMomentId;
	const currentIndex = snapshot.findIndex((e) => e.observedMomentId === activeObservedMomentId);
	const currentEntry = currentIndex >= 0 ? snapshot[currentIndex] : undefined;

	// "All memories…" expansion is per card: paging or skipping to another
	// moment collapses the previous-memories list back to the preview. Reset
	// synchronously during render (not in an effect) so the first paint of the
	// next card never flashes the previous card's expanded list.
	const [showAllMemories, setShowAllMemories] = useState(false);
	const [expandedForMomentId, setExpandedForMomentId] = useState(activeObservedMomentId);
	if (expandedForMomentId !== activeObservedMomentId) {
		setExpandedForMomentId(activeObservedMomentId);
		setShowAllMemories(false);
	}

	// Mark the active moment seen the first time it's viewed, keyed by the
	// observed moment (BIZY-673): the backend opens the moment's notification
	// row, creating a pre-opened one for moments that never had a row, so the
	// sidebar's unread dot clears for every entry.
	const seenRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		if (!currentEntry || !currentEntry.unread) return;
		if (seenRef.current.has(currentEntry.observedMomentId)) return;
		seenRef.current.add(currentEntry.observedMomentId);
		markSeen(currentEntry.observedMomentId);
	}, [currentEntry, markSeen]);

	// Left/right arrows page through the deck (BIZY-665), stopping at the ends
	// rather than exiting. Keystrokes aimed at form fields or at focused
	// elements outside the person view (the main app stays interactive under
	// the compact panel) are left alone.
	const rootRef = useRef<HTMLDivElement>(null);

	// Focus the view whenever a card opens — the click that opened it (a
	// sidebar entry, including reselects while the view stays mounted in
	// fullscreen) leaves focus outside the root, which the guard below would
	// treat as "aimed elsewhere", so arrows wouldn't page until a first click
	// inside the card (BIZY-671).
	useEffect(() => {
		rootRef.current?.focus({ preventScroll: true });
	}, [activeObservedMomentId]);

	useEffect(() => {
		const handleArrowKey = (event: KeyboardEvent) => {
			if (event.defaultPrevented || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
			const target = event.target instanceof HTMLElement ? event.target : null;
			if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
			if (target && target !== document.body && rootRef.current && !rootRef.current.contains(target)) return;
			const entry = snapshot[currentIndex + (event.key === 'ArrowRight' ? 1 : -1)];
			if (!entry) return;
			event.preventDefault();
			viewPerson({ id: entry.person.id, name: entry.person.name ?? '', observedMomentId: entry.observedMomentId });
		};
		window.addEventListener('keydown', handleArrowKey);
		return () => window.removeEventListener('keydown', handleArrowKey);
	}, [snapshot, currentIndex, viewPerson]);

	if (!viewedPerson) return null;

	const person = momentsData?.person ?? currentEntry?.person;
	const name = viewedPerson.name || person?.name || t('personView.unknownPerson');

	// Prefer the full moment record; fall back to the sidebar entry's headline so
	// the card still renders if the per-person endpoint fails (while loading, the
	// card shows a skeleton instead of this fallback).
	const activeMoment: Partial<BizyPersonMoment> | undefined =
		momentsData?.moments.find((m) => m.id === activeObservedMomentId) ??
		(currentEntry ? { headline: currentEntry.headline, moment: null, source: currentEntry.source, createdAt: currentEntry.sentAt } : undefined);

	const title = activeMoment?.headline ?? currentEntry?.headline ?? '';
	const body = activeMoment?.moment ?? '';
	const source = activeMoment?.source ?? currentEntry?.source ?? null;
	const momentDate = toDate(activeMoment?.createdAt ?? currentEntry?.sentAt);

	// Feedback controls copy the headline + body and light the current thumb.
	// Normalize the wire's `null` (never rated) to `undefined` for the shared
	// component's prop.
	const feedbackContent = [title, body].filter(Boolean).join('\n\n');
	const momentFeedbackType = activeMoment?.feedbackType ?? undefined;

	const total = snapshot.length;
	const position = currentIndex >= 0 ? currentIndex + 1 : 1;
	// Up to two ghost cards behind the active one (per design, the 3rd+ card is
	// hidden), so the deck thins to one on the next-to-last card, none on the last.
	const remainingCards = Math.min(Math.max(total - position, 0), 2);
	// The narrow side panel gets the stacked layout from the design's compact
	// frame; fullscreen (and the always-full-window desktop MFE) gets the wide
	// card layout.
	const isCompact = !isFullscreen && !fullSurface;

	const handleNext = () => {
		const next = snapshot[currentIndex + 1];
		if (next) {
			viewPerson({ id: next.person.id, name: next.person.name ?? '', observedMomentId: next.observedMomentId });
		} else {
			// Nothing left to advance to — return to the chat.
			clearViewedPerson();
		}
	};

	const handleDiveIn = () => {
		if (!activeObservedMomentId) return;
		sendChatMessage(
			buildRecognizeMomentChatPayload({
				name,
				title,
				body,
				source,
				personId: person?.id ?? viewedPerson.id,
				avatarUrl: person?.avatarUrl,
				observedMomentId: activeObservedMomentId,
				t,
			})
		);
		clearViewedPerson();
	};

	const backToList = () => {
		showHistory();
		clearViewedPerson();
	};

	// Feedback handlers mirror the message-feedback flow: positive/clear submit
	// immediately, negative defers to the modal. `personId` is `viewedPerson.id`
	// so the optimistic cache patch targets the same query key as
	// `usePersonMoments` above.
	const handlePositiveMomentFeedback = (observedMomentId: string) => {
		if (!viewedPerson) return;
		track(AnalyticsEvent.Bizy.FeedbackPositive, { source: 'noticed', path: window.location.pathname });
		submitMomentFeedback({ personId: viewedPerson.id, observedMomentId, feedbackType: 'positive' });
	};

	const handleNegativeMomentFeedback = (observedMomentId: string) => {
		track(AnalyticsEvent.Bizy.FeedbackNegativeStarted, { source: 'noticed', path: window.location.pathname });
		setFeedbackMomentId(observedMomentId);
		setIsFeedbackModalOpen(true);
	};

	const handleClearMomentFeedback = (observedMomentId: string) => {
		if (!viewedPerson) return;
		track(AnalyticsEvent.Bizy.FeedbackCleared, { source: 'noticed', path: window.location.pathname });
		submitMomentFeedback({ personId: viewedPerson.id, observedMomentId, feedbackType: null });
	};

	const handleFeedbackModalSubmit = (category: string, details?: string) => {
		if (!viewedPerson || !feedbackMomentId) return;
		track(AnalyticsEvent.Bizy.FeedbackNegativeSubmitted, { source: 'noticed', category, path: window.location.pathname });
		submitMomentFeedback(
			{
				personId: viewedPerson.id,
				observedMomentId: feedbackMomentId,
				feedbackType: 'negative',
				feedbackCategory: category,
				feedbackDetails: details,
			},
			{
				// Close only after the write lands (mirrors message feedback); on
				// failure the modal stays open with its selections so the user can retry.
				onSuccess: () => {
					setIsFeedbackModalOpen(false);
					setFeedbackMomentId(null);
				},
			}
		);
	};

	// Only show the controls once the active moment's full record has loaded (not
	// during the skeleton or a paging refetch). This also guarantees the moments
	// query cache exists, so the mutation's optimistic `setQueryData` patch
	// always lands instead of no-opping against an empty cache.
	const activeMomentLoaded = momentsData?.moments.some((m) => m.id === activeObservedMomentId) ?? false;
	const feedbackControls =
		activeObservedMomentId && activeMomentLoaded ? (
			<BizyMessageFeedback
				messageId={activeObservedMomentId}
				content={feedbackContent}
				feedbackType={momentFeedbackType}
				onPositiveFeedback={handlePositiveMomentFeedback}
				onNegativeFeedback={handleNegativeMomentFeedback}
				onClearFeedback={handleClearMomentFeedback}
			/>
		) : null;

	// Previous moments = this person's other moments not already surfaced in the
	// sidebar's current set (and not the active one).
	const sidebarMomentIds = new Set(snapshot.map((e) => e.observedMomentId));
	const previousMoments = (momentsData?.moments ?? []).filter((m) => m.id !== activeObservedMomentId && !sidebarMomentIds.has(m.id));
	const visiblePreviousMoments = showAllMemories ? previousMoments : previousMoments.slice(0, PREVIOUS_MEMORIES_PREVIEW_COUNT);
	const hasMorePreviousMoments = !showAllMemories && previousMoments.length > PREVIOUS_MEMORIES_PREVIEW_COUNT;

	return (
		<Flex
			ref={rootRef}
			tabIndex={-1}
			outline="none"
			data-component="bizy-person-view"
			direction="column"
			h="100%"
			minH={0}
			bg="bg.surface"
			position="relative"
			overflow="hidden"
		>
			{/* Ambient glow behind the header and card (BIZY-665). */}
			<Box position="absolute" inset={0} pointerEvents="none" aria-hidden overflow="hidden" sx={{ '.chakra-ui-dark &': { opacity: 0.15 } }}>
				{GLOW_BLOBS.map((blob) => (
					<Box key={blob.bg} position="absolute" top={blob.top} left={blob.left} w={blob.w} h={blob.h} bg={blob.bg} borderRadius="full" filter="blur(100px)" />
				))}
			</Box>
			<BizySidePanelHeader
				pl={(isFullscreen && !isPeopleBrowserCollapsed) || fullSurface ? 5 : 3}
				isDragRegion={fullSurface}
				left={
					<HStack spacing={1} minW={0}>
						{/* Compact: opens the People list menu. Fullscreen: reopens the
						    collapsed People sidebar (hidden while the sidebar is visible).
						    Desktop full-surface navigates people via the app sidebar, so it
						    shows no header menu button at all. */}
						{!fullSurface && (!isFullscreen || isPeopleBrowserCollapsed) && (
							<BizyHeaderIconButton
								label={isFullscreen ? t('sidePanel.openBizyMenu') : t('personView.backToList')}
								icon={<MenuAlt2Outline boxSize={5} />}
								onClick={isFullscreen ? togglePeopleBrowserCollapsed : backToList}
								isNoDrag={fullSurface}
							/>
						)}
						<Text fontSize="md" fontWeight="semibold" color="text.default" noOfLines={1} mt={fullSurface ? 6 : undefined}>
							{t('menu.bizyNoticed')}
						</Text>
					</HStack>
				}
				right={
					<HStack spacing={0}>
						{showFullscreenToggle && (
							<BizyHeaderIconButton
								label={isFullscreen ? t('sidePanel.exitFullscreen') : t('sidePanel.fullscreen')}
								icon={isFullscreen ? <MinimizeOutline boxSize={4} /> : <MaximizeOutline boxSize={4} />}
								onClick={toggleFullscreen}
								isNoDrag={fullSurface}
							/>
						)}
						<BizyHeaderIconButton
							label={t('sidePanel.close')}
							icon={<XOutline boxSize={5} />}
							onClick={() => (onClose ? onClose() : closePanel())}
							isNoDrag={fullSurface}
						/>
					</HStack>
				}
			/>

			<Box flex={1} minH={0} overflowY="auto" px={4} pt={fullSurface ? 2 : 4} pb={4} position="relative">
				{/* mt leaves room for the ghost cards (and, compact, the bee) peeking
				    above the active card. */}
				<HStack align="flex-start" spacing={0} maxW="640px" mx="auto" mt={isCompact ? 6 : 4}>
					{/* Wide layout: the bee sits to the left, overlapping the card's edge
					    (negative margin keeps it in flow so it can't force horizontal
					    scroll) and looks toward the person card. */}
					{!isCompact && (
						<Box flexShrink={0} mt={-2} mr={-8} position="relative" zIndex={1} display={{ base: 'none', sm: 'block' }}>
							<Bizy state="lookingRight" size={100} />
						</Box>
					)}

					<Box flex={1} minW={0} position="relative">
						{/* Compact layout: the bee peeks over the card's top-right corner,
						    partly hidden behind it. */}
						{isCompact && (
							<Box position="absolute" top="-36px" right={3} pointerEvents="none">
								<Bizy state="facingLeft" size={56} />
							</Box>
						)}

						{/* The next (up to two) cards peek out behind the active one at 40%
						    opacity, each with a small stable-random rotation and offset seeded
						    from its moment id — paging reveals a fresh tilt per card (bottom
						    peek only in compact, where the bee owns the card's top edge).
						    Deepest renders first so the nearer card paints over it. */}
						{Array.from({ length: remainingCards }, (_, index) => {
							const depth = remainingCards - index;
							const seed = snapshot[currentIndex + depth]?.observedMomentId ?? `depth-${depth}`;
							const rotation = ((momentJitter(seed, 2) < 0.5 ? -1 : 1) * (0.5 + momentJitter(seed, 1) * 1.5)).toFixed(2);
							const peek = Math.round(4 + momentJitter(seed, 3) * 6 + (depth - 1) * 4);
							const shiftX = Math.round((momentJitter(seed, 4) - 0.5) * 16);
							return (
								<Box
									key={seed}
									data-component="bizy-person-ghost-card"
									position="absolute"
									top={isCompact ? '8px' : `-${peek}px`}
									bottom={`-${peek}px`}
									left={`${shiftX}px`}
									right={`${-shiftX}px`}
									bg="bg.surface"
									opacity={0.4}
									borderRadius="xl"
									boxShadow="base"
									transform={`rotate(${rotation}deg)`}
									pointerEvents="none"
								/>
							);
						})}

						{/* Active moment card */}
						<Box position="relative" borderRadius="xl" bg="bg.surface" boxShadow="base" p={5} data-component="bizy-person-moment-card">
							{isCompact ? (
								<HStack align="center" spacing={3}>
									<Avatar name={name} src={person?.avatarUrl ?? undefined} size="md" />
									<Box minW={0}>
										<Text fontSize="md" fontWeight="semibold" color="text.default" noOfLines={1}>
											{name}
										</Text>
										{momentDate && <RelativeTime date={momentDate} format="short" fontSize="sm" color="text.softest" />}
									</Box>
								</HStack>
							) : (
								<HStack align="center" spacing={2}>
									<Avatar name={name} src={person?.avatarUrl ?? undefined} size="sm" />
									<Text fontSize="sm" fontWeight="semibold" color="text.default" noOfLines={1}>
										{name}
									</Text>
									<Spacer />
									{momentDate && <RelativeTime date={momentDate} format="short" fontSize="xs" color="text.softest" />}
								</HStack>
							)}

							{/* Render the headline and body together once the full moment record
							    arrives; a skeleton bridges the fetch so the body doesn't pop in
							    under an already-visible headline. */}
							{isLoadingMoments ? (
								<SkeletonText mt={4} noOfLines={3} spacing={2} skeletonHeight="14px" />
							) : (
								<>
									{title && (
										<Text fontSize="sm" fontWeight="semibold" color="text.default" mt={3}>
											{title}
										</Text>
									)}
									{body && (
										<Text fontSize="sm" color="text.soft" mt={1} lineHeight="tall">
											{body}
										</Text>
									)}

									{source && (
										<HStack spacing={1} mt={3}>
											<BizySourceIcon source={source} />
										</HStack>
									)}
								</>
							)}

							{isCompact ? (
								<Box mt={4}>
									<Box borderWidth="1px" borderColor="border.soft" borderRadius="full" py={1.5} textAlign="center">
										<Text fontSize="md" fontWeight="medium" color="text.default">
											{t('personView.positionCount', { position, total })}
										</Text>
									</Box>
									{/* Narrow panel: the icons get their own left-aligned row under
									    the position pill (no room beside the wide action buttons). */}
									{feedbackControls && <Flex justify="center">{feedbackControls}</Flex>}
									<HStack spacing={3} mt={3}>
										<Button flex={1} variant="outline" colorScheme="secondary" onClick={handleNext}>
											{t('personView.next')}
										</Button>
										<Button flex={1} variant="solid" colorScheme="primary" onClick={handleDiveIn}>
											{t('personView.letsDiveIn')}
										</Button>
									</HStack>
								</Box>
							) : (
								<HStack align="center" mt={4} pt={4} borderTopWidth="1px" borderColor="border.soft" spacing={2}>
									<Box borderWidth="1px" borderColor="border.soft" borderRadius="full" px={3} py={0.5}>
										<Text fontSize="sm" fontWeight="medium" color="text.default" whiteSpace="nowrap">
											{t('personView.positionCount', { position, total })}
										</Text>
									</Box>
									{/* Cancel the shared component's built-in top margin so the icons
									    sit centered in this single-row footer. */}
									{feedbackControls && <Box mt={-3}>{feedbackControls}</Box>}
									<Spacer />
									<Button variant="outline" colorScheme="secondary" size="sm" onClick={handleNext}>
										{t('personView.next')}
									</Button>
									<Button variant="solid" colorScheme="primary" size="sm" onClick={handleDiveIn}>
										{t('personView.letsDiveIn')}
									</Button>
								</HStack>
							)}
						</Box>
					</Box>
				</HStack>

				{previousMoments.length > 0 && (
					<Box maxW="640px" mx="auto" mt={14}>
						<Text fontSize="sm" fontWeight="semibold" color="text.default" mb={2}>
							{t('personView.previousMemories', { name })}
						</Text>
						{/* Keyed per card: the uncontrolled Accordion tracks its expanded row
						    by index, which would otherwise leak across paging and auto-open
						    an unrelated row on the next card. */}
						<Accordion key={activeObservedMomentId} allowToggle>
							{visiblePreviousMoments.map((moment) => {
								const when = toDate(moment.createdAt);
								return (
									<AccordionItem key={moment.id} borderColor="border.soft">
										<AccordionButton px={2} py={3}>
											<HStack flex={1} spacing={2} minW={0} textAlign="left">
												<Text fontSize="sm" color="text.default" noOfLines={1} flex={1}>
													{moment.headline ? stripLeadingFirstName(moment.headline, name) : moment.headline}
												</Text>
												<BizySourceIcon source={moment.source} size="16px" />
												{when && <RelativeTime date={when} format="short" fontSize="xs" color="text.softest" />}
												<AccordionIcon color="text.softest" />
											</HStack>
										</AccordionButton>
										{moment.moment && (
											<AccordionPanel px={2} pb={3}>
												<Text fontSize="sm" color="text.soft" lineHeight="tall">
													{moment.moment}
												</Text>
											</AccordionPanel>
										)}
									</AccordionItem>
								);
							})}
						</Accordion>

						{hasMorePreviousMoments && (
							<Box pt={3}>
								<Button variant="link" size="sm" color="text.interactive" onClick={() => setShowAllMemories(true)}>
									{t('menu.allMemories')}
								</Button>
							</Box>
						)}
					</Box>
				)}
			</Box>

			<BizyFeedbackModal
				isOpen={isFeedbackModalOpen}
				onClose={() => {
					track(AnalyticsEvent.Bizy.FeedbackNegativeCancelled, { source: 'noticed', path: window.location.pathname });
					setIsFeedbackModalOpen(false);
					setFeedbackMomentId(null);
				}}
				onSubmit={handleFeedbackModalSubmit}
				categories={MOMENT_FEEDBACK_CATEGORIES}
				privacyNoticeKey="feedback.momentPrivacyNotice"
			/>
		</Flex>
	);
}
